//src/components/modules/Dialog
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Steps,
  Button,
  Typography,
  Card,
  List,
  Avatar,
  Input,
  Space,
  Divider,
  Radio,
  Form,
  message,
  Alert,
  Dropdown,
  Menu,
  Row,
  Col,
  Select
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  DownOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import preguntasObservaciones from "./preguntasObservacionesDialog";
import observacionesEditables from "./observacionesEditablesDialog";
import nativeWebSocket from "../../../websocket-native";
import { respuestasCierre } from "./respuestasCierre";
import OTPVerificationDialog from "./OTPVerificationDialog";

const { Step } = Steps;
const { Title, Text } = Typography;
const { TextArea } = Input;

function SeguimientoTrasladoDialog({
  open,
  onClose,
  trasladoId,
  status,
  seguimientoEstado,
  onValidar,
  onCerrar,
  onObservacionAgregada 
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [observaciones, setObservaciones] = useState([]);
  const [nuevaObservacion, setNuevaObservacion] = useState("");
  const [filtroPregunta, setFiltroPregunta] = useState("");
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // Estados locales para manejar la UI
  const [isValidated, setIsValidated] = useState(seguimientoEstado === "Validado");
  const [isClosed, setIsClosed] = useState(false);
  
  // Estados para el cierre
  const [estadoCierre, setEstadoCierre] = useState(
    status === "Programado" ? "Cancelado" : status
  );
  const [mensajeCierre, setMensajeCierre] = useState("");

  // Estado para el diálogo OTP
  const [showOTPDialog, setShowOTPDialog] = useState(false);

  // Estados cerrados definidos con useMemo para evitar recreación innecesaria
  const ESTADOS_CERRADOS = useMemo(() =>
    ["Cancelado", "Finalizado", "Fallido"],
    []
  );

  // Opciones del mensaje de cierre basadas en el estado seleccionado
  const opcionesMensajeCierre = useMemo(() => {
    const opciones = respuestasCierre[estadoCierre] || [];
    return opciones.map((respuesta) => ({
      label: respuesta,
      value: respuesta
    }));
  }, [estadoCierre]);

  // Sincronizar estados con las props
  useEffect(() => {
    setIsValidated(seguimientoEstado === "Validado");
    setIsClosed(ESTADOS_CERRADOS.includes(status));
  }, [seguimientoEstado, status, ESTADOS_CERRADOS]);

  // Cargar observaciones y resetear estado al abrir el modal
  useEffect(() => {
    if (trasladoId && open) {
      api.get(`/observaciones/${Number(trasladoId)}`)
        .then((response) => {
          setObservaciones(response.data);
        })
        .catch((error) => {
          console.error("Error al obtener las observaciones:", error);
        });
      
      setCurrentStep(0);
      setEstadoCierre(status === "Programado" ? "Cancelado" : status);
      setMensajeCierre("");
    }
  }, [trasladoId, open, status]);

  // Integración WebSocket para actualizaciones en tiempo real de observaciones
  useEffect(() => {
    if (!trasladoId || !open) return;

    // Callback para actualizaciones de observaciones específicas de este traslado
    const handleObservacionesUpdate = (data) => {
      const { trasladoId: updatedTrasladoId } = data;
      
      // Solo actualizar si es para este traslado específico (comparar como números)
      if (Number(updatedTrasladoId) === Number(trasladoId)) {
        // Recargar observaciones cuando se detecte una actualización
        api.get(`/observaciones/${Number(trasladoId)}`)
          .then((response) => {
            setObservaciones(response.data);
          })
          .catch((error) => {
            console.error("Error al recargar observaciones:", error);
          });
      }
    };

    // Registrar el listener
    nativeWebSocket.onObservacionesUpdate(handleObservacionesUpdate);

    // Cleanup no es necesario ya que los listeners se acumulan globalmente
    // y es la intención para múltiples componentes escuchando
    
  }, [trasladoId, open]);

 const handleAgregarObservacion = async (texto = nuevaObservacion) => {
  if (!texto.trim() || !user) return;

  try {
    // Crear nueva observación con datos mínimos para actualización inmediata
    const nuevaObs = {
      id: `temp-${Date.now()}`, // ID temporal
      texto_observacion: texto,
      created_at: new Date().toISOString(),
      Usuario: { nombre: user.nombre }
    };
    
    // Actualizar lista inmediatamente (UI optimista)
    setObservaciones(prev => [...prev, nuevaObs]);
    setNuevaObservacion(""); // Limpiar el campo
    
    // Enviar al servidor - esto disparará el evento WebSocket automáticamente
    await api.post("/observaciones", {
      traslado_id: Number(trasladoId), // Convertir a número para el backend
      usuario_id: user.id,
      texto_observacion: texto,
    });

    // La actualización real vendrá vía WebSocket, por lo que no necesitamos
    // actualizar manualmente - el useEffect con WebSocket lo hará
    
    message.success("Observación agregada correctamente");
    
    // Notificar al componente padre si hay callback
    if (onObservacionAgregada) {
      onObservacionAgregada();
    }
    
  } catch (error) {
    console.error("Error al guardar:", error);
    message.error("Error al guardar en servidor");
    
    // En caso de error, revertir la actualización optimista
    // recargando las observaciones del servidor
    try {
      const response = await api.get(`/observaciones/${trasladoId}`);
      setObservaciones(response.data);
    } catch (reloadError) {
      console.error("Error al recargar observaciones:", reloadError);
    }
  }
};

  const handleMenuClick = (key) => {
    if (observacionesEditables.includes(key)) {
      setNuevaObservacion(key);
    } else {
      handleAgregarObservacion(key);
    }
  };

  const preguntasFiltradas = preguntasObservaciones.filter((pregunta) => 
    pregunta.toLowerCase().includes(filtroPregunta.toLowerCase())
  );

  const handleNext = async () => {
    if (currentStep === 2) {
      // Validar que se haya seleccionado un motivo de cierre
      if (!mensajeCierre) {
        message.error("Debe seleccionar un motivo de cierre");
        return;
      }

      // Validar regla de negocio: Estados permitidos
      const estadosPermitidos = status === "Programado"
        ? ["Cancelado"]
        : ["Finalizado", "Cancelado", "Fallido"];

      if (!estadosPermitidos.includes(estadoCierre)) {
        message.error(`Estado inválido para un traslado en estado: ${status}`);
        return;
      }

      if (!onCerrar) {
        message.error("Acción no disponible");
        return;
      }

      // Si es "Finalizado", abrir diálogo OTP para verificación
      if (estadoCierre === "Finalizado") {
        setShowOTPDialog(true);
        return;
      }

      // Para otros estados (Cancelado, Fallido), cerrar directamente
      try {
        await onCerrar(estadoCierre, mensajeCierre.trim());
        setIsClosed(true);

        message.success({
          content: (
            <div>
              <Title level={5} style={{ color: "#52c41a" }}>
                Traslado Cerrado Exitosamente
              </Title>
              <Text>
                El traslado <strong>CS-{trasladoId}</strong> ha sido cerrado como
                <strong> {estadoCierre}</strong>
              </Text>
            </div>
          ),
          duration: 5,
        });

        onClose();
      } catch (err) {
        console.error("Error cerrando traslado:", err);
        message.error("No fue posible cerrar el traslado");
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  // Manejar verificación OTP exitosa
  const handleOTPVerified = () => {
    setIsClosed(true);
    setShowOTPDialog(false);

    message.success({
      content: (
        <div>
          <Title level={5} style={{ color: "#52c41a" }}>
            Traslado Finalizado con Verificación OTP
          </Title>
          <Text>
            El traslado <strong>CS-{trasladoId}</strong> ha sido cerrado como
            <strong> Finalizado</strong> con verificación exitosa
          </Text>
        </div>
      ),
      duration: 5,
    });

    onClose();
  };

  const handlePrev = () => {
    // Navegar al paso anterior
    setCurrentStep(currentStep - 1);
  };

  const handleValidar = async () => {
  if (!onValidar) return;
  try {
    // Esperar que el handler del padre complete (haga la PUT y emita)
    await onValidar(); 
    setIsValidated(true);

    message.success({
      content: (
        <div>
          <Title level={5} style={{ color: "#52c41a" }}>
            Validación Exitosa
          </Title>
          <Text>
            El traslado <strong>CS-{trasladoId}</strong> ha sido validado correctamente
          </Text>
        </div>
      ),
      duration: 3,
    });
  } catch (err) {
    console.error("Error validando traslado:", err);
    message.error("Error al validar el traslado");
  }
};

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: 
        return (
          <div>
            <Card
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                backgroundColor: "#fafafa",
                padding: "24px",
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
            >
              <List
                dataSource={observaciones}
                renderItem={(observacion) => (
                  <List.Item key={observacion.id} style={{ padding: 0 }}>
                    <List.Item.Meta
                      avatar={
                        observacion.Usuario ? (
                          <Avatar style={{ backgroundColor: "#52c41a" }} icon={null}>
                            {observacion.Usuario.nombre.charAt(0).toUpperCase()}
                          </Avatar>
                        ) : (
                          <Avatar icon={<UserOutlined />} />
                        )
                      }
                      title={
                        <Space>
                          <Text strong style={{ color: "#2E3B4E" }}>
                            {observacion.Usuario
                              ? observacion.Usuario.nombre
                              : "Usuario desconocido"}
                          </Text>
                          <Text type="secondary" style={{ fontStyle: "italic" }}>
                            <CalendarOutlined />{" "}
                            {new Date(observacion.created_at).toLocaleString()}
                          </Text>
                        </Space>
                      }
                      description={
                        <Card
                          style={{
                            backgroundColor:
                              observacion.Usuario &&
                              observacion.Usuario.nombre === user.nombre
                                ? "#e6f7ff"
                                : "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                            marginTop: "8px",
                          }}
                        >
                          <Text>{observacion.texto_observacion}</Text>
                        </Card>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
            
            <Divider style={{ margin: "24px 0" }} />
            
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <TextArea
                rows={4}
                placeholder="Escribe tu observación..."
                value={nuevaObservacion}
                onChange={(e) => setNuevaObservacion(e.target.value)}
                style={{
                  borderRadius: "8px",
                  padding: "10px",
                  resize: "none",
                }}
              />
              <Row gutter={8}>
                <Col span={12}>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleAgregarObservacion()}
                    disabled={!nuevaObservacion.trim()}
                    block
                  >
                    Agregar Observación
                  </Button>
                </Col>
                <Col span={12}>
                  <Dropdown
                    trigger={["click"]}
                    dropdownRender={() => (
                      <div style={{ padding: "8px", width: "300px" }}>
                        <Input
                          placeholder="Buscar observación rápida"
                          value={filtroPregunta}
                          onChange={(e) => setFiltroPregunta(e.target.value)}
                          style={{ marginBottom: "8px" }}
                        />
                        <Menu
                          onClick={({ key }) => handleMenuClick(key)}
                          style={{ maxHeight: "200px", overflowY: "auto" }}
                        >
                          {preguntasFiltradas.map((pregunta) => (
                            <Menu.Item key={pregunta}>{pregunta}</Menu.Item>
                          ))}
                        </Menu>
                      </div>
                    )}
                  >
                    <Button icon={<DownOutlined />} block>
                      Insertar Observación Rápida
                    </Button>
                  </Dropdown>
                </Col>
              </Row>
            </Space>
          </div>
        );
      
      case 1: // Paso de Validación
        return (
          <Card
            style={{
              backgroundColor: "#fafafa",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Text type="secondary">
                  Traslado con el siguiente <strong>CS-ID:</strong>
                </Text>
                <Title
                  level={3}
                  style={{
                    backgroundColor: "#e6f7ff",
                    padding: "12px",
                    borderRadius: "8px",
                    textAlign: "center",
                    color: "#1890ff",
                    marginTop: "8px",
                  }}
                >
                  CS-{trasladoId || "ID no disponible"}
                </Title>
              </div>
              <Divider />
              
              {isValidated ? (
                <Alert
                  message={`Traslado Validado - CS-${trasladoId}`}
                  type="success"
                  showIcon
                  style={{ width: "100%" }}
                />
              ) : (
                <>
                  <Space align="center">
                    <SafetyCertificateOutlined style={{ fontSize: "20px", color: "#52c41a" }} />
                    <Text>
                      ¿Estás seguro de validar el traslado CS-{trasladoId}? Esta acción cambiará su seguimiento a "Validado".
                    </Text>
                  </Space>
                  
                  <div style={{ marginTop: 24 }}>
                    <Button 
                      type="primary" 
                      icon={<SafetyCertificateOutlined />}
                      onClick={handleValidar}
                      block
                    >
                      Validar Traslado CS-{trasladoId}
                    </Button>
                  </div>
                </>
              )}
            </Space>
          </Card>
        );
      
      case 2: // Paso de Cierre
        // Verificar si el traslado ya está cerrado (Finalizado, Fallido o Cancelado)
        if (ESTADOS_CERRADOS.includes(status)) {
          return (
            <Card
              style={{
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                padding: "24px",
                borderRadius: "12px",
              }}
            >
              <Alert
                message="Servicio Cerrado"
                description={
                  <>
                    <Text>
                      Este traslado ya ha sido cerrado con el estado: <strong>{status}</strong>
                    </Text>
                    <br />
                    <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                      No es posible modificar el estado de cierre.
                    </Text>
                  </>
                }
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={onClose}
                >
                  Cerrar
                </Button>
              </div>
            </Card>
          );
        }
        
        return (
          <Form form={form} layout="vertical">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text type="secondary" style={{ marginBottom: 20 }}>
                Selecciona el estado con el que deseas cerrar el traslado:
              </Text>

              <Form.Item
                name="estado"
                rules={[
                  {
                    required: true,
                    message: "Por favor selecciona un estado",
                  },
                ]}
              >
                <Radio.Group
                  onChange={(e) => {
                    setEstadoCierre(e.target.value);
                    setMensajeCierre(""); // Limpiar mensaje al cambiar estado
                    form.setFieldsValue({ mensaje: undefined });
                  }}
                  value={estadoCierre}
                  disabled={status === "Programado"}
                >
                  <Space direction="vertical">
                    {status === "Programado" ? (
                      <Radio value="Cancelado">Cancelado</Radio>
                    ) : (
                      <>
                        <Radio value="Finalizado">
                          <Space>
                            Finalizado
                            <SafetyOutlined style={{ color: "#722ed1" }} />
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              (Requiere verificación OTP)
                            </Text>
                          </Space>
                        </Radio>
                        <Radio value="Cancelado">Cancelado</Radio>
                        <Radio value="Fallido">Fallido</Radio>
                      </>
                    )}
                  </Space>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="mensaje"
                label={`Motivo del cierre (${opcionesMensajeCierre.length} opciones disponibles para ${estadoCierre})`}
                rules={[
                  {
                    required: true,
                    message: "Por favor selecciona el motivo del cierre",
                  },
                ]}
                style={{ marginTop: "20px", width: "100%" }}
              >
                <Select
                  key={`select-cierre-${estadoCierre}`}
                  showSearch
                  placeholder="Selecciona o busca el motivo del cierre"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                  value={mensajeCierre || undefined}
                  onChange={(value) => setMensajeCierre(value)}
                  notFoundContent="No se encontraron opciones"
                  allowClear
                  options={opcionesMensajeCierre}
                />
              </Form.Item>
            </Space>
          </Form>
        );
      
      default:
        return null;
    }
  };

  const getFooter = () => {
    if (currentStep === 2 && isClosed) {
      return null;
    }
    
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button 
          onClick={handlePrev} 
          disabled={currentStep === 0}
        >
          Anterior
        </Button>
        
        <Button
          type="primary"
          onClick={handleNext}
          icon={currentStep === 2 ? (estadoCierre === "Finalizado" ? <SafetyOutlined /> : <CloseOutlined />) : <CheckCircleOutlined />}
          disabled={currentStep === 2 && isClosed}
          style={currentStep === 2 && estadoCierre === "Finalizado" ? { backgroundColor: "#722ed1", borderColor: "#722ed1" } : {}}
        >
          {currentStep === 2
            ? (estadoCierre === "Finalizado" ? "Verificar con OTP" : "Cerrar Traslado")
            : "Siguiente"}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={getFooter()}
        title={
          <div>
            <Title level={4} style={{ marginBottom: 0 }}>Seguimiento de Traslado</Title>
            <Steps current={currentStep} size="small" style={{ marginTop: 16 }}>
              <Step title={`Observaciones (${observaciones.length})`} />
              <Step title="Validar" />
              <Step title="Cerrar" />
            </Steps>
          </div>
        }
        width={700}
        centered
        destroyOnClose
      >
        {renderStepContent()}
      </Modal>

      {/* Diálogo de verificación OTP para finalizar */}
      <OTPVerificationDialog
        open={showOTPDialog}
        onClose={() => setShowOTPDialog(false)}
        onVerified={handleOTPVerified}
        trasladoId={trasladoId}
        mensaje={mensajeCierre}
        tipo="normal"
      />
    </>
  );
}

SeguimientoTrasladoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  status: PropTypes.string.isRequired,
  seguimientoEstado: PropTypes.string.isRequired,
  onValidar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

export default SeguimientoTrasladoDialog;